// Subscription billing pieces shared by the unified license list (OrderList):
// the Sub type + loader, the lifetime-upgrade panel, and the copy-key row. The former
// standalone SubscriptionList section was folded into the license cards.
import { useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Center,
  Checkbox,
  CopyButton,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCircleCheck, IconCheck, IconCopy } from "@tabler/icons-react";
import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import { useTranslation } from "react-i18next";
import { PAYPAL_CLIENT_ID } from "../../../config/paypal";
import { findPlan } from "../../../config/plans";
import { getLineTotal, unitPrice, FULL_PRICE_PCS } from "../PayPalCheckout/UseCart";
import { Edition } from "../../Settings";
import { DEBUG } from "../..";
import classes from "./Manager.module.css";

export interface Sub {
  subscription_id: string;
  app: string;
  edition: Edition;
  duration: string;
  quantity: number;
  plan: string;
  status: string;
  next_billing_date: string | null;
  cancelled: boolean;
  key_ident?: string;
  pending_quantity?: number | null;
}

export type Status = "idle" | "saving" | "success" | "error";

export const editionLabel = (e: Edition) => (e === "pro" ? "LMU TV Pro" : "LMU TV Standard");

// The license key the customer enters in the app to unlock it, with a copy button.
export function KeyReveal({ ident, label }: { ident: string; label: string }) {
  return (
    <Group gap={4} wrap="nowrap" align="center">
      <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>{label}:</Text>
      <Text size="xs" style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{ident}</Text>
      <CopyButton value={ident} timeout={1500}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Copied" : "Copy"} withArrow position="right">
            <ActionIcon size="sm" variant="subtle" color={copied ? "teal" : "gray"} onClick={copy}>
              {copied ? <IconCheck size={13} /> : <IconCopy size={13} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

export async function loadSubs(): Promise<Sub[]> {
  try {
    const r = await fetch("/dyn/get-subscriptions.php", { credentials: "include" });
    const d = await r.json();
    return Array.isArray(d) ? d : [];
  } catch (e) {
    if (DEBUG) console.error("loadSubs", e);
    return [];
  }
}

// ── One-time PayPal capture that upgrades the sub to lifetime ─────────────────
function UpgradeButton({ sub, disabled, onStatus }: {
  sub: Sub; disabled: boolean; onStatus: (s: Status) => void;
}) {
  const [{ isPending }] = usePayPalScriptReducer();
  if (isPending) return <Center py="sm"><Loader color="lmuTVColors" /></Center>;

  const unit = unitPrice(sub.edition, "lifetime");
  return (
    // Solid dark bg so PayPal paints the button iframe body dark (no white gaps in the modal).
    <div style={{ position: "relative", backgroundColor: "#242424" }}  className={classes.managerContainer}>
      <PayPalButtons
        style={{ layout: "vertical", color: "black", shape: "rect" }}
        disabled={disabled}
        createOrder={(_d, actions) => {
          const fullQty = Math.min(sub.quantity, FULL_PRICE_PCS);
          const items: any[] = [{
            name: editionLabel(sub.edition) + " (Lifetime)", description: `${sub.edition}-lifetime-upgrade`,
            category: "DIGITAL_GOODS", quantity: fullQty,
            unit_amount: { value: unit.toFixed(2), currency_code: "EUR" },
          }];
          if (sub.quantity > FULL_PRICE_PCS) items.push({
            name: editionLabel(sub.edition) + " (50% bulk)", description: `${sub.edition}-lifetime-upgrade`,
            category: "DIGITAL_GOODS", quantity: sub.quantity - FULL_PRICE_PCS,
            unit_amount: { value: (unit / 2).toFixed(2), currency_code: "EUR" },
          });
          const total = getLineTotal({ edition: sub.edition, duration: "lifetime", pcs: sub.quantity });
          return actions.order.create({
            intent: "CAPTURE",
            purchase_units: [{ amount: { value: total.toFixed(2), currency_code: "EUR", breakdown: { item_total: { value: total.toFixed(2), currency_code: "EUR" } } }, items }],
          });
        }}
        onApprove={async (_d, actions) => {
          onStatus("saving");
          try {
            const details = await actions.order!.capture();
            const fd = new FormData();
            fd.append("subscription_id", sub.subscription_id);
            fd.append("order_id", details.id!);
            const r = await fetch("/dyn/upgrade-order.php", { method: "POST", body: fd, credentials: "include" });
            onStatus((await r.json()).success === true ? "success" : "error");
          } catch (e) { if (DEBUG) console.error(e); onStatus("error"); }
        }}
        onError={() => onStatus("error")}
      />
      {disabled && <div style={{ position: "absolute", inset: 0, backdropFilter: "blur(3px)", background: "rgba(0,0,0,0.15)", borderRadius: 4, cursor: "not-allowed", zIndex: 10 }} />}
    </div>
  );
}

export function UpgradePanel({ sub, status, setStatus, onDone }: {
  sub: Sub; status: Status; setStatus: (s: Status) => void; onDone: () => void;
}) {
  const { t } = useTranslation("translation");
  const [tos, setTos] = useState(false);
  const plan = findPlan("lmutv", sub.edition, "lifetime");
  const total = getLineTotal({ edition: sub.edition, duration: "lifetime", pcs: sub.quantity });

  if (status === "success") {
    return (
      <Alert icon={<IconCircleCheck size={20} />} color="green" radius="md" title={t("account.subscriptions.successTitle")}>
        <Text size="sm" mb="sm">{t("account.subscriptions.successMsg")}</Text>
        <Button color="lmuTVColors" onClick={onDone} fullWidth>{t("menu.user.account")}</Button>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">{t("account.subscriptions.upgradeIntro")}</Text>
      <Group justify="space-between">
        <Text fw={600}>{editionLabel(sub.edition)} · {sub.quantity} {t("cart.pcs")}</Text>
        <Text fw={700} size="lg">{total.toFixed(2)}€</Text>
      </Group>
      {sub.quantity > FULL_PRICE_PCS && <Text size="xs" c="lmuTVColors.4">{t("cart.bulkNote")}</Text>}
      <Divider />

      {status === "error" && (
        <Alert color="red" radius="md" withCloseButton onClose={() => setStatus("idle")}>
          {t("account.subscriptions.errorMsg")}
        </Alert>
      )}

      {!plan ? (
        <Alert color="yellow" radius="md">{t("subscribe.notAvailable")}</Alert>
      ) : (
        <>
          <Checkbox
            checked={tos}
            onChange={() => setTos((v) => !v)}
            label={
              <Text size="xs">
                {t("subscribe.tos.prefix")}{" "}
                <Anchor href="/tos" target="_blank" size="xs">{t("subscribe.tos.tos")}</Anchor>{" "}
                {t("subscribe.tos.and")}{" "}
                <Anchor href="/privacy_web" target="_blank" size="xs">{t("subscribe.tos.privacy")}</Anchor>
              </Text>
            }
          />
          <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "EUR", intent: "capture", disableFunding: "credit" }}>
            <UpgradeButton sub={sub} disabled={!tos || status === "saving"} onStatus={setStatus} />
          </PayPalScriptProvider>
        </>
      )}
    </Stack>
  );
}
